import { useEffect, useState, useCallback } from "react";
import OwnerLayout from "../layout/OwnerLayout";
import { API_BASE_URL } from "../../config/api";
import { authHeader } from "../../utils/authHeader";

const LEVEL_CONFIG = {
  red:    { label: "🔴 Red Alert",  bg: "#fee2e2", border: "#ef4444", color: "#991b1b", badge: "#ef4444" },
  orange: { label: "🟠 Warning",    bg: "#fff7ed", border: "#f97316", color: "#9a3412", badge: "#f97316" },
  green:  { label: "🟢 Notice",     bg: "#f0fdf4", border: "#22c55e", color: "#166534", badge: "#22c55e" },
};

// ── Alert Popup Modal ─────────────────────────────
const AlertModal = ({ alert, onClose, onReplySuccess }) => {
  const [replyText,    setReplyText]    = useState("");
  const [replyImage,   setReplyImage]   = useState(null);
  const [replyPreview, setReplyPreview] = useState("");
  const [sending,      setSending]      = useState(false);
  const [replyMsg,     setReplyMsg]     = useState("");

  if (!alert) return null;
  const cfg = LEVEL_CONFIG[alert.level] || LEVEL_CONFIG.green;

  const handleReply = async () => {
    if (!replyText.trim()) return setReplyMsg("⚠️ Please type a message.");
    setSending(true); setReplyMsg("");

    const formData = new FormData();
    formData.append("message", replyText);
    if (replyImage) formData.append("image", replyImage);

    try {
      const res  = await fetch(`${API_BASE_URL}/api/alerts/${alert._id}/reply`, {
        method:  "POST",
        headers: authHeader(),
        body:    formData,
      });
      const data = await res.json();
      if (res.ok) {
        setReplyMsg("✅ Reply sent!");
        setReplyText(""); setReplyImage(null); setReplyPreview("");
        if (onReplySuccess) onReplySuccess();
      } else {
        setReplyMsg(`⚠️ ${data.message}`);
      }
    } catch { setReplyMsg("⚠️ Server error"); }
    setSending(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "20px", width: "100%", maxWidth: "560px", maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}
        onClick={e => e.stopPropagation()}>

        {/* ── Colored header based on level ── */}
        <div style={{ background: cfg.bg, borderBottom: `3px solid ${cfg.border}`, padding: "18px 20px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <span style={{ background: cfg.badge, color: "#fff", padding: "3px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: "700" }}>{cfg.label}</span>
            <h3 style={{ margin: "8px 0 0", color: "#1b2559", fontSize: "16px", lineHeight: "1.4" }}>{alert.title}</h3>
            <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#6b7280" }}>
              {new Date(alert.createdAt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
          <button onClick={onClose} style={{ background: "rgba(0,0,0,0.08)", border: "none", borderRadius: "8px", padding: "6px 10px", cursor: "pointer", fontSize: "16px", color: "#374151", flexShrink: 0, marginLeft: "10px" }}>✕</button>
        </div>

        {/* ── Scrollable content ── */}
        <div style={{ overflowY: "auto", flex: 1, padding: "20px" }}>

          {/* Message */}
          <p style={{ fontSize: "15px", color: "#374151", lineHeight: "1.8", margin: "0 0 16px" }}>{alert.message}</p>

          {/* Proof Image */}
          {alert.imageUrl && (
            <div style={{ marginBottom: "16px" }}>
              <p style={{ fontSize: "12px", fontWeight: "700", color: "#6b7280", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>📷 Proof Image</p>
              <img src={alert.imageUrl} alt="proof" onClick={() => window.open(alert.imageUrl, "_blank")}
                style={{ width: "100%", borderRadius: "12px", border: "1px solid #e0e5f2", cursor: "pointer" }} />
              <p style={{ fontSize: "11px", color: "#a3aed0", textAlign: "center", marginTop: "4px" }}>Click to open full size</p>
            </div>
          )}

          {/* Video / Link */}
          {alert.videoUrl && (
            <div style={{ marginBottom: "16px" }}>
              <p style={{ fontSize: "12px", fontWeight: "700", color: "#6b7280", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>🔗 Video / Link</p>
              <a href={!/^https?:\/\//i.test(alert.videoUrl) ? "https://" + alert.videoUrl : alert.videoUrl} target="_blank" rel="noreferrer"
                style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px 16px", background: "#f0f4ff", borderRadius: "10px", border: "1px solid #c7d2fe", textDecoration: "none", color: "#4318ff", fontWeight: "600", fontSize: "14px" }}>
                <span style={{ fontSize: "20px" }}>▶</span>
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Watch Video / Open Link</span>
              </a>
            </div>
          )}

          {/* Your previous reply */}
          {alert.myReply && (
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "10px", padding: "12px 14px", marginBottom: "16px" }}>
              <p style={{ fontSize: "12px", fontWeight: "700", color: "#166534", marginBottom: "6px" }}>✅ Your Reply (already sent)</p>
              <p style={{ fontSize: "14px", color: "#374151", margin: 0 }}>{alert.myReply.message}</p>
              {alert.myReply.imageUrl && <img src={alert.myReply.imageUrl} alt="reply" style={{ marginTop: "8px", maxWidth: "100%", borderRadius: "8px" }} />}
              {/* Admin's response to owner reply */}
              {alert.myReply.adminReply?.message && (
                <div style={{ marginTop: "10px", background: "#f0f4ff", border: "1px solid #c7d2fe", borderRadius: "8px", padding: "10px 12px" }}>
                  <p style={{ fontSize: "12px", fontWeight: "700", color: "#4318ff", marginBottom: "4px" }}>👑 Admin replied to you</p>
                  <p style={{ fontSize: "14px", color: "#374151", margin: 0 }}>{alert.myReply.adminReply.message}</p>
                  <p style={{ fontSize: "11px", color: "#a3aed0", marginTop: "4px", marginBottom: 0 }}>{new Date(alert.myReply.adminReply.repliedAt).toLocaleString("en-IN")}</p>
                </div>
              )}
            </div>
          )}

          {/* Reply box — only if admin allowed AND owner hasn't replied yet */}
          {alert.allowReply && !alert.myReply && (
            <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: "16px", marginTop: "8px" }}>
              <p style={{ fontSize: "13px", fontWeight: "700", color: "#1b2559", marginBottom: "10px" }}>💬 Reply to Admin</p>

              {replyMsg && (
                <div style={{ padding: "8px 12px", borderRadius: "8px", marginBottom: "10px", fontSize: "13px", background: replyMsg.startsWith("✅") ? "#f0fdf4" : "#fee2e2", color: replyMsg.startsWith("✅") ? "#166534" : "#991b1b" }}>
                  {replyMsg}
                </div>
              )}

              <textarea value={replyText} onChange={e => setReplyText(e.target.value)}
                placeholder="Type your reply here..."
                rows={3}
                style={{ width: "100%", padding: "10px 14px", borderRadius: "10px", border: "1.5px solid #e0e5f2", fontSize: "14px", outline: "none", resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" }} />

              {/* Reply image upload */}
              <div style={{ marginTop: "8px", marginBottom: "12px" }}>
                <label style={{ fontSize: "13px", color: "#6b7280", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px", padding: "6px 12px", background: "#f4f7fe", borderRadius: "8px", border: "1px solid #e0e5f2" }}>
                  📷 Attach Image (optional)
                  <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
                    const file = e.target.files[0];
                    if (!file) return;
                    setReplyImage(file);
                    setReplyPreview(URL.createObjectURL(file));
                  }} />
                </label>
                {replyPreview && (
                  <div style={{ marginTop: "8px", position: "relative", display: "inline-block" }}>
                    <img src={replyPreview} alt="preview" style={{ maxWidth: "120px", borderRadius: "8px" }} />
                    <button onClick={() => { setReplyImage(null); setReplyPreview(""); }}
                      style={{ position: "absolute", top: "-6px", right: "-6px", background: "#ef4444", color: "#fff", border: "none", borderRadius: "50%", width: "18px", height: "18px", cursor: "pointer", fontSize: "10px" }}>✕</button>
                  </div>
                )}
              </div>

              <button onClick={handleReply} disabled={sending}
                style={{ padding: "10px 20px", background: "#4318ff", color: "#fff", border: "none", borderRadius: "10px", fontWeight: "600", fontSize: "14px", cursor: "pointer", width: "100%" }}>
                {sending ? "Sending..." : "📤 Send Reply"}
              </button>
            </div>
          )}

          {alert.allowReply && alert.myReply && (
            <p style={{ textAlign: "center", fontSize: "13px", color: "#a3aed0", marginTop: "8px" }}>
              You have already replied to this alert
            </p>
          )}

          {!alert.allowReply && (
            <p style={{ textAlign: "center", fontSize: "13px", color: "#a3aed0", marginTop: "8px" }}>
              🔒 Replies are not enabled for this alert
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Notifications Page ───────────────────────
const OwnerNotifications = () => {
  const [alerts,      setAlerts]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [filter,      setFilter]      = useState("all");
  const [activeAlert, setActiveAlert] = useState(null);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/alerts/my`, { headers: authHeader() });
      const data = await res.json();
      if (res.ok) setAlerts(data.alerts || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const openAlert = async (alert) => {
    setActiveAlert(alert);
    // Mark as read when opened
    if (!alert.isRead) {
      try {
        await fetch(`${API_BASE_URL}/api/alerts/${alert._id}/read`, { method: "PUT", headers: authHeader() });
        setAlerts(prev => prev.map(a => a._id === alert._id ? { ...a, isRead: true } : a));
      } catch {}
    }
  };

  const markAllRead = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/alerts/mark-all-read`, { method: "PUT", headers: authHeader() });
      setAlerts(prev => prev.map(a => ({ ...a, isRead: true })));
    } catch {}
  };

  const unreadCount = alerts.filter(a => !a.isRead).length;
  const filtered    = alerts.filter(a => {
    if (filter === "unread") return !a.isRead;
    if (filter === "read")   return  a.isRead;
    return true;
  });

  return (
    <OwnerLayout>
      <div className="dashboard-header">
        <p className="welcome-text">Alerts & Notices</p>
        <h2 className="company-name">
          Notifications
          {unreadCount > 0 && (
            <span style={{ marginLeft: "10px", background: "#ef4444", color: "#fff", borderRadius: "12px", padding: "2px 10px", fontSize: "14px", fontWeight: "700" }}>
              {unreadCount} new
            </span>
          )}
        </h2>
      </div>

      <div className="owner-dashboard-wrapper">
        <div className="owner-fraud-container">

          {/* Filter + Mark All Read */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", marginBottom: "20px" }}>
            <div style={{ display: "flex", gap: "8px" }}>
              {["all", "unread", "read"].map(f => (
                <button key={f} onClick={() => setFilter(f)}
                  style={{ padding: "7px 16px", borderRadius: "20px", border: "1.5px solid", cursor: "pointer", fontSize: "13px", fontWeight: "600", background: filter === f ? "#4318ff" : "#f4f7fe", color: filter === f ? "#fff" : "#1b2559", borderColor: filter === f ? "#4318ff" : "#e0e5f2" }}>
                  {f === "all" ? `All (${alerts.length})` : f === "unread" ? `Unread (${unreadCount})` : `Read (${alerts.length - unreadCount})`}
                </button>
              ))}
            </div>
            {unreadCount > 0 && (
              <button onClick={markAllRead}
                style={{ padding: "7px 16px", background: "#f0fdf4", color: "#166534", border: "1.5px solid #22c55e", borderRadius: "20px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>
                ✓ Mark All Read
              </button>
            )}
          </div>

          {/* Alert Cards */}
          {loading ? (
            <p style={{ color: "#a3aed0", textAlign: "center", padding: "40px" }}>Loading…</p>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px", color: "#a3aed0" }}>
              <div style={{ fontSize: "48px", marginBottom: "12px" }}>🔔</div>
              <p style={{ fontWeight: "600", color: "#1b2559" }}>{filter === "unread" ? "No unread notifications" : "No notifications yet"}</p>
              <p style={{ fontSize: "13px" }}>Admin will send alerts when needed</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {filtered.map(alert => {
                const cfg = LEVEL_CONFIG[alert.level] || LEVEL_CONFIG.green;
                return (
                  <div key={alert._id} onClick={() => openAlert(alert)}
                    style={{ background: alert.isRead ? "#fafafa" : "#fff", borderRadius: "14px", padding: "16px 18px", border: `2px solid ${alert.isRead ? "#e0e5f2" : cfg.border}`, boxShadow: alert.isRead ? "none" : "0 2px 10px rgba(0,0,0,0.07)", cursor: "pointer", opacity: alert.isRead ? 0.8 : 1, transition: "all 0.2s" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px", alignItems: "center" }}>
                          <span style={{ background: cfg.badge, color: "#fff", padding: "2px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "700" }}>{cfg.label}</span>
                          {!alert.isRead && <span style={{ background: "#4318ff", color: "#fff", padding: "2px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "700" }}>NEW</span>}
                          {alert.allowReply && !alert.myReply && <span style={{ fontSize: "11px", background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0", padding: "2px 8px", borderRadius: "10px" }}>💬 Reply allowed</span>}
                          {alert.myReply   && <span style={{ fontSize: "11px", background: "#f0f4ff", color: "#4318ff", border: "1px solid #c7d2fe", padding: "2px 8px", borderRadius: "10px" }}>✅ Replied</span>}
                          {alert.imageUrl  && <span style={{ fontSize: "11px", color: "#6b7280" }}>📷</span>}
                          {alert.videoUrl  && <span style={{ fontSize: "11px", color: "#6b7280" }}>🔗</span>}
                          <span style={{ fontSize: "11px", color: "#a3aed0" }}>
                            {new Date(alert.createdAt).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                        <h4 style={{ margin: "0 0 4px", color: "#1b2559", fontSize: "15px" }}>{alert.title}</h4>
                        <p style={{ margin: 0, color: "#6b7280", fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{alert.message}</p>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", flexShrink: 0 }}>
                        {!alert.isRead && <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: cfg.badge }} />}
                        <span style={{ fontSize: "18px", color: "#a3aed0" }}>›</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Popup Modal */}
      {activeAlert && (
        <AlertModal
          alert={activeAlert}
          onClose={() => setActiveAlert(null)}
          onReplySuccess={() => { fetchAlerts(); setActiveAlert(null); }}
        />
      )}
    </OwnerLayout>
  );
};

export default OwnerNotifications;
